import { inject, observer } from 'mobx-react';
import React, { Fragment, useRef, useState } from 'react';
import { Modal } from 'reactstrap';

import { modelOf } from '../../../prop-types';
import CountryStore from '../../../store/CountryStore';
import CurrencyStore from '../../../store/CurrencyStore';
import LanguageStore from '../../../store/LanguageStore';
import { countryCodeFromLocale } from '../../../util/languageMapper';
import LocaleFlagIcon from '../../common/LocaleFlagIcon';
import RegionalSettings from '../../common/RegionalSettings';
import RegionalSettingsDialogContent from '../../common/RegionalSettingsDialogContent';
import HeaderNavAction from '../HeaderNavAction';

const NavigationRegionalSettingsMenu = ({
  languageStore,
  currencyStore,
  countryStore,
}) => {
  const [open, setOpen] = useState(false);
  const dialogBodyRef = useRef(null);
  const shouldRestoreFocusRef = useRef(false);
  const triggerRef = useRef(null);

  const showCountries = countryStore.countries.length > 1;
  const showLanguages = languageStore.languages.length > 1;
  const showCurrencies = currencyStore.currencies.length > 1;
  const selectorCount = [showCountries, showLanguages, showCurrencies].filter(
    Boolean
  ).length;

  const titleId = 'navigation-regional-settings-dialog-title';
  const labels = [];
  if (showCountries) {
    labels.push(countryStore.activeCountry.name);
  }
  if (showLanguages) {
    labels.push(languageStore.activeLanguage.name);
  }
  if (showCurrencies) {
    labels.push(currencyStore.currencyCode);
  }

  if (labels.length === 0) {
    return null;
  }

  const canRenderModal = typeof window !== 'undefined' && !window.isSSR;

  const labelIcon = showCountries ? (
    <LocaleFlagIcon
      locale={countryStore.activeCountry.iso_code_2.toLowerCase()}
      decorative
    />
  ) : (
    <LocaleFlagIcon
      locale={countryCodeFromLocale(languageStore.activeLanguage.code)}
      decorative
    />
  );

  const closeDialog = ({ restoreFocus = true } = {}) => {
    shouldRestoreFocusRef.current = restoreFocus;
    setOpen(false);
  };

  const handleDialogClosed = () => {
    if (
      shouldRestoreFocusRef.current &&
      triggerRef.current &&
      typeof triggerRef.current.focus === 'function'
    ) {
      triggerRef.current.focus();
    }
    shouldRestoreFocusRef.current = false;
  };

  const handleDialogOpened = () => {
    if (
      dialogBodyRef.current &&
      typeof dialogBodyRef.current.focus === 'function'
    ) {
      dialogBodyRef.current.focus();
    }
  };

  const handleDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeDialog();
    }
  };

  return (
    <RegionalSettings>
      {(save, onChange, currentValues, isSaving, errorState) => {
        return (
          <Fragment>
            <li
              className={`NavigationRegionalSettingsMenu NavigationRegionalSettingsMenu--selectors-${selectorCount} nav-item`}
            >
              <HeaderNavAction
                ref={triggerRef}
                surface="top"
                className="NavigationRegionalSettingsMenu__trigger dropdown-toggle"
                aria-haspopup="dialog"
                onClick={() => setOpen(true)}
              >
                <span className="NavigationRegionalSettingsMenu__dropdown-current-label">
                  {labelIcon}
                  {labels.join(' / ')}
                </span>
              </HeaderNavAction>
            </li>
            {canRenderModal && (
              <Modal
                isOpen={open}
                toggle={() => closeDialog()}
                className="NavigationRegionalSettingsMenu__dialog"
                labelledBy={titleId}
                onOpened={handleDialogOpened}
                onClosed={handleDialogClosed}
                fade={false}
              >
                <RegionalSettingsDialogContent
                  currentValues={currentValues}
                  onChange={onChange}
                  errorState={errorState}
                  isSaving={isSaving}
                  onSave={() => {
                    save().then((success) => {
                      if (success) {
                        closeDialog();
                      }
                    });
                  }}
                  onClose={() => closeDialog()}
                  titleId={titleId}
                  contentRef={dialogBodyRef}
                  contentTabIndex={-1}
                  onContentKeyDown={handleDialogKeyDown}
                />
              </Modal>
            )}
          </Fragment>
        );
      }}
    </RegionalSettings>
  );
};

NavigationRegionalSettingsMenu.propTypes = {
  languageStore: modelOf(LanguageStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
};

export default inject(
  'languageStore',
  'currencyStore',
  'countryStore'
)(observer(NavigationRegionalSettingsMenu));
